import { createRouter } from "@tanstack/react-router";
import qs from "qs";

import { REACT_QUERY_CLIENT } from "components/app_layout/AppProviders";
import { NotFoundPage } from "components/not_found/NotFoundPage";
import { createAuthedClient } from "lib/hooks/api/graphQlClientFactory";
import { qsSettings } from "lib/hooks/useURLState";
import * as tracking from "lib/tracking";
import { accountRouteTree } from "./routes/account";
import { adminToolsRouteTree } from "./routes/adminTools";
import { authRoutes } from "./routes/auth";
import { betterAuthRoutes } from "./routes/betterAuth";
import {
  bidResponsesRouteTree,
  bidsRouteTree,
  sharedBidReportRoute,
  sharedQualificationReportRoute,
} from "./routes/bids";
import { buyersRouteTree } from "./routes/buyers";
import { contactsRouteTree } from "./routes/contacts";
import { contentLibraryRouteTree } from "./routes/contentLibrary";
import { documentsRouteTree } from "./routes/documents";
import { frameworksRouteTree } from "./routes/frameworks";
import { guestAccessRetiredRoute } from "./routes/guestAccessRetired";
import { homeRoute } from "./routes/home";
import { leadsRouteTree } from "./routes/leads";
import { noticeDetailRouteTree, noticesRouteTree, noticesV2Route } from "./routes/notices";
import { onboardingRoute } from "./routes/onboarding";
import { reportsRouteTree } from "./routes/reports";
import { savedSearchesRoute } from "./routes/savedSearches";
import { spendRouteTree } from "./routes/spend";
import { suppliersRouteTree } from "./routes/suppliers";
import { unsubscribeRoute } from "./routes/unsubscribe";
import { uploadContextLayoutRoute } from "./routes/uploadContext";
import { rootRoute } from "./rootRoute";

declare module "@tanstack/react-router" {
  interface Register {
    router: typeof router;
  }
}

const routeTree = rootRoute.addChildren([
  homeRoute,
  adminToolsRouteTree,
  onboardingRoute,
  uploadContextLayoutRoute.addChildren([accountRouteTree, contentLibraryRouteTree]),
  bidsRouteTree,
  bidResponsesRouteTree,
  sharedBidReportRoute,
  sharedQualificationReportRoute,
  leadsRouteTree,
  buyersRouteTree,
  frameworksRouteTree,
  contactsRouteTree,
  noticesRouteTree,
  noticesV2Route,
  noticeDetailRouteTree,
  reportsRouteTree,
  documentsRouteTree,
  savedSearchesRoute,
  spendRouteTree,
  suppliersRouteTree,
  guestAccessRetiredRoute,
  unsubscribeRoute,
  ...authRoutes,
  ...betterAuthRoutes,
]);

export const router = createRouter({
  routeTree,
  // Rails answers the wildcard SPA prefixes with the shell at HTTP 200, so an
  // unmatched in-app path reaches the router rather than Rails' 404. Without
  // this it rendered the router's bare built-in "Not Found" text.
  defaultNotFoundComponent: NotFoundPage,
  // Default to an anonymous client; <AuthedRouter> supplies the real, token-authed client via
  // RouterProvider's `context` prop (merged in before any loader runs). Using a real anonymous
  // client as the default — rather than a null placeholder — keeps the type honest and is a safe
  // fallback. See pages/app/RouterApp.tsx.
  context: { queryClient: REACT_QUERY_CLIENT, graphQLClient: createAuthedClient("") },
  parseSearch: (search) => qs.parse(search, qsSettings) as Record<string, unknown>,
  stringifySearch: (search) =>
    qs.stringify(search, {
      arrayFormat: "brackets",
      encodeValuesOnly: true,
      addQueryPrefix: true,
    }),
});

router.subscribe("onResolved", ({ toLocation }) => {
  // Use the deepest matched route (last match), skipping the root layout
  const match = router.state.matches.at(-1);
  const pageName = match?.staticData?.pageName;
  if (pageName) {
    tracking.pageView(pageName, { path: toLocation.pathname });
  }
});
